import { EventEmitter } from "events";
import EditQuery, { AivisSpeechQuery, AivisSpeechSpeakerInfo } from "./AivisSpeechUtils";
import { defaultAivisSpeechInfo } from "~/vrm_utils/VRMUtils";
import { api } from "../../api";
import { audioManager, tryPlayAudio } from "~/SoundUtils";
import { removeDollarKeys } from "~/utils/chatMessageUtils";
import { showNotificationDialog } from "~/utils/notificationDialogUtils";
import { t } from "i18next";

export const emitter = new EventEmitter();

// 音声追跡用のグローバル変数
let lastAudioId = 0;
let currentPlayingAudioId: number | null = null;

/**
 * AivisSpeechを使用してテキストを音声合成し再生する
 *
 * @param inputText 音声合成するテキスト
 * @param setIsThinking 思考中フラグの更新関数
 * @param setIsProcessing 処理中フラグの更新関数
 * @param setIsTranscribing 文字起こし有効フラグの更新関数
 * @param setOutputText 出力テキストの更新関数
 * @param speakerInfo AivisSpeech話者情報
 * @param setIsInConversationCycle 会話サイクルフラグの更新関数
 * @param endExpression 再生終了時にemitterで通知する表情（デフォルト: "neutral"）。
 *                      "bow" を指定すると発話終了後にお辞儀モーションが再生される。
 */
const handleAivisSynthesize = async (
  inputText: string,
  setIsThinking?: (flag: boolean) => void,
  setIsProcessing?: (flag: boolean) => void,
  setIsTranscribing?: (flag: boolean) => void,
  setOutputText?: (text: string) => void,
  speakerInfo: AivisSpeechSpeakerInfo = defaultAivisSpeechInfo,
  setIsInConversationCycle?: (flag: boolean) => void,
  endExpression: string = "neutral",
  playbackToken?: number
) => {
  const getLabData = (jsondata: AivisSpeechQuery): string => {
    let labdata = "";
    let nowLength = 0;
    const timescale = 10000000 / jsondata.speedScale;

    jsondata.accent_phrases.forEach((phrase) => {
      phrase.moras.forEach((mora) => {
        if (mora.consonant_length !== null) {
          labdata += `${Math.round(nowLength * timescale)} `;
          nowLength += mora.consonant_length;
          labdata += `${Math.round(nowLength * timescale)} ${mora.consonant}\n`;
        }
        labdata += `${Math.round(nowLength * timescale)} `;
        nowLength += mora.vowel_length;
        labdata += `${Math.round(nowLength * timescale)} ${mora.vowel}\n`;
      });

      if (phrase.pause_mora !== null) {
        labdata += `${Math.round(nowLength * timescale)} `;
        nowLength += phrase.pause_mora.vowel_length;
        labdata += `${Math.round(nowLength * timescale)} pau\n`;
      }
    });

    return labdata;
  };

  const playAndGetLabData = (labData: string, audio: HTMLAudioElement) => {
    // 各音声に一意のIDを割り当て
    const audioId = ++lastAudioId;
    currentPlayingAudioId = audioId;

    let intervalId: number | null = null;
    audio.addEventListener("play", () => {
      // 音声再生中は定期的に口を動かす
      intervalId = window.setInterval(() => {
        const vowels = ["a", "e", "i", "o", "u"];
        const randomVowel = vowels[Math.floor(Math.random() * vowels.length)];
        emitter.emit("phoneme", randomVowel);
      }, 150);
    });

    audio.addEventListener("ended", () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
      emitter.emit("expression", endExpression); // 表情を通知
      emitter.emit("phoneme", "end"); // 再生終了を通知
      if (setIsThinking) setIsThinking(false);

      // この音声が最新の音声か判定
      if (audioId === currentPlayingAudioId) {
        setTimeout(() => {
          // タイムアウト後も最新の音声なら文字起こしを有効化
          if (audioId === currentPlayingAudioId) {
            if (setIsTranscribing) setIsTranscribing(true);
            currentPlayingAudioId = null;
          }
        }, 1500);
      }
    });

    // 再生終了時にaudioManagerをクリア
    audio.addEventListener("ended", () => {
      audioManager.clearAudio();
    });

    // if (setOutputText) setOutputText(inputText);
    // audioManagerに登録してから再生
    audioManager.setAudio(audio);
    tryPlayAudio(audio);
  };

  try {
    // AIVISSPEECHのAPIを使用してクエリを作成
    const createQuery = async (audioInputText: string) => {
      try {
        const queryJson = await api.aivisspeech.audioQuery.mutate({
          speaker: speakerInfo.id[0],
          text: audioInputText,
        });

        if (!queryJson) return { queryJson: null, labdata: null };
        const labdata = getLabData(queryJson as AivisSpeechQuery);

        return { queryJson, labdata };
      } catch (error) {
        console.error("Error creating query:", error);
        throw error;
      }
    };

    // AIVISSPEECHのAPIを使用して音声を合成
    const createVoice = async (queryJson: AivisSpeechQuery) => {
      try {
        const result = await api.aivisspeech.synthesis.mutate({
          speaker: speakerInfo.id[0],
          queryJson: queryJson,
        });

        if (!result) return null;

        // Base64をBlobに変換
        const byteArray = Uint8Array.from(atob(result.audioData), (c) => c.charCodeAt(0));
        return new Blob([byteArray], { type: "audio/wav" });
      } catch (error) {
        console.error("Error creating voice:", error);
        throw error;
      }
    };

    // $$key$$ を取り除いたテキストを音声合成に使用
    const audioInputText = removeDollarKeys(inputText);

    const { queryJson, labdata } = await createQuery(audioInputText);
    if (!queryJson || !labdata) return;
    const editedQuery = EditQuery(queryJson as AivisSpeechQuery, speakerInfo);
    if (!editedQuery) return;

    const audioData = await createVoice(editedQuery);
    if (!audioData) return;
    const audioUrl = URL.createObjectURL(audioData);

    // 合成中に画面遷移などで別の再生がリクエストされた場合、この音声は古いので
    // 再生もチャット出力も行わずに破棄する
    if (playbackToken !== undefined && !audioManager.isLatestPlayback(playbackToken)) {
      console.info("AIVISPEECH TTS - Skipping stale audio (playback superseded)");
      URL.revokeObjectURL(audioUrl);
      if (setIsThinking) setIsThinking(false);
      return;
    }

    const audio = new Audio(audioUrl);

    playAndGetLabData(labdata, audio);

    // チャット出力
    if (setOutputText) setOutputText(inputText);
  } catch (error) {
    console.error("Error occurred while synthesizing text:", error);
    if (setIsThinking) setIsThinking(false);
    if (setIsProcessing) setIsProcessing(false);
    if (setOutputText) setOutputText(t("audioPlaybackError"));
    if (setIsInConversationCycle) setIsInConversationCycle(false);
    showNotificationDialog(t("audioPlaybackErrorTitle"), "error");
  }
};

export default handleAivisSynthesize;
